/**
 * Shopify Context Provider
 * 
 * Provides cart functionality using Shopify Storefront API (shopify-buy SDK).
 * Handles cart creation, adding/removing items, and checkout redirect.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Client from 'shopify-buy';

// Initialize Shopify client - only if credentials are present
const hasShopifyCredentials = process.env.REACT_APP_SHOPIFY_DOMAIN && process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;

const client = hasShopifyCredentials ? Client.buildClient({
  domain: process.env.REACT_APP_SHOPIFY_DOMAIN,
  storefrontAccessToken: process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN,
}) : null;

const ShopifyContext = createContext();

export const ShopifyProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize or restore cart
  useEffect(() => {
    const initCart = async () => {
      // Skip initialization if no Shopify credentials
      if (!client) {
        console.log('Shopify: No credentials configured, cart disabled');
        setLoading(false);
        return;
      }
      
      try {
        const cartId = localStorage.getItem('shopifyCartId');
        if (cartId) {
          try {
            const existingCart = await client.checkout.fetch(cartId);
            // Check if cart exists and is not completed
            if (existingCart && !existingCart.completedAt) {
              setCart(existingCart);
              setLoading(false);
              return;
            } else {
              // Cart was completed (order placed), clear it and create new one
              console.log('Shopify: Previous cart was completed, creating new one');
              localStorage.removeItem('shopifyCartId');
            }
          } catch (fetchError) {
            console.log('Shopify: Could not fetch existing cart, creating new one', fetchError?.message);
            localStorage.removeItem('shopifyCartId');
          }
        }
        
        // Create new cart
        const newCart = await client.checkout.create();
        localStorage.setItem('shopifyCartId', newCart.id);
        setCart(newCart);
      } catch (err) {
        console.error('Shopify cart initialization failed:', err);
        // Handle "body stream already read" error by clearing cart and retrying
        if (err.message?.includes('body stream already read') || err.message?.includes('body has already been consumed')) {
          console.log('Shopify: Clearing corrupted cart state');
          localStorage.removeItem('shopifyCartId');
          try {
            const newCart = await client.checkout.create();
            localStorage.setItem('shopifyCartId', newCart.id);
            setCart(newCart);
            setLoading(false);
            return;
          } catch (retryErr) {
            console.error('Shopify: Retry failed', retryErr);
          }
        }
        // Check for specific error types
        if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
          setError('Invalid Shopify credentials. Please check your Storefront Access Token in admin settings.');
        } else {
          setError(err.message || 'Failed to initialize Shopify cart');
        }
      } finally {
        setLoading(false);
      }
    };
    
    initCart();
  }, []);

  // Add item to cart
  const addToCart = useCallback(async (variantId, quantity = 1) => {
    if (!client) {
      console.error('Shopify client not initialized');
      return { success: false, error: 'Shopify not configured' };
    }
    if (!cart) {
      console.error('Cart not initialized - this may be due to invalid Shopify credentials');
      return { success: false, error: 'Cart not available. Please check Shopify credentials.' };
    }
    
    try {
      const lineItems = [{ variantId, quantity }];
      const newCart = await client.checkout.addLineItems(cart.id, lineItems);
      setCart(newCart);
      setCartOpen(true);
      return { success: true };
    } catch (error) {
      console.error('Error adding to cart:', error);
      // Check for specific Shopify errors
      if (error.message?.includes('Variant not found') || error.message?.includes('variant')) {
        return { success: false, error: 'Product variant not found. Please check the Variant ID.' };
      }
      return { success: false, error: error.message || 'Failed to add item to cart' };
    }
  }, [cart]);

  // Remove item from cart
  const removeFromCart = useCallback(async (lineItemId) => {
    if (!cart || !client) return;
    
    try {
      const newCart = await client.checkout.removeLineItems(cart.id, [lineItemId]);
      setCart(newCart);
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  }, [cart]);

  // Update item quantity
  const updateQuantity = useCallback(async (lineItemId, quantity) => {
    if (!cart || !client) return;
    
    try {
      if (quantity < 1) {
        await removeFromCart(lineItemId);
        return;
      }
      const lineItems = [{ id: lineItemId, quantity }];
      const newCart = await client.checkout.updateLineItems(cart.id, lineItems);
      setCart(newCart);
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }, [cart, removeFromCart]);

  // Clear cart
  const clearCart = useCallback(async () => {
    if (!client) return;
    
    try {
      const newCart = await client.checkout.create();
      localStorage.setItem('shopifyCartId', newCart.id);
      setCart(newCart);
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  }, []);

  // Reset cart - force create new cart (for error recovery)
  const resetCart = useCallback(async () => {
    localStorage.removeItem('shopifyCartId');
    setError(null);
    setCart(null);
    
    if (!client) return;
    
    try {
      const newCart = await client.checkout.create();
      localStorage.setItem('shopifyCartId', newCart.id);
      setCart(newCart);
    } catch (error) {
      console.error('Error resetting cart:', error);
      setError('Failed to reset cart. Please refresh the page.');
    }
  }, []);

  // Fetch product with variants from Shopify (bypasses cache)
  const fetchProduct = useCallback(async (productId) => {
    if (!client) return null;
    
    try {
      // Format the product ID as a Shopify GID if needed
      let gid = productId;
      if (!productId.includes('gid://')) {
        gid = `gid://shopify/Product/${productId}`;
      }
      
      // Use direct GraphQL call with timestamp to bypass browser cache
      const query = `
        query getProduct($id: ID!) {
          node(id: $id) {
            ... on Product {
              id
              title
              description
              descriptionHtml
              handle
              availableForSale
              images(first: 10) {
                edges {
                  node {
                    id
                    src
                    altText
                  }
                }
              }
              variants(first: 100) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    priceV2 {
                      amount
                      currencyCode
                    }
                    selectedOptions {
                      name
                      value
                    }
                    image {
                      id
                      src
                      altText
                    }
                  }
                }
              }
              options {
                id
                name
                values
              }
            }
          }
        }
      `;
      
      // Add timestamp to URL to bust browser cache
      const timestamp = Date.now();
      const response = await fetch(
        `https://${process.env.REACT_APP_SHOPIFY_DOMAIN}/api/2024-01/graphql.json?_t=${timestamp}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN,
          },
          body: JSON.stringify({
            query,
            variables: { id: gid }
          })
        }
      );
      
      const data = await response.json();
      
      if (data.data?.node) {
        // Transform to match shopify-buy SDK format
        const node = data.data.node;
        return {
          id: node.id,
          title: node.title,
          description: node.description,
          descriptionHtml: node.descriptionHtml,
          handle: node.handle,
          availableForSale: node.availableForSale,
          images: node.images?.edges?.map(e => e.node) || [],
          variants: node.variants?.edges?.map(e => ({
            id: e.node.id,
            title: e.node.title,
            available: e.node.availableForSale,
            priceV2: e.node.priceV2,
            selectedOptions: e.node.selectedOptions,
            image: e.node.image
          })) || [],
          // Transform options values to match shopify-buy SDK format: {value: "X"}
          options: node.options?.map(opt => ({
            id: opt.id,
            name: opt.name,
            values: opt.values.map(v => ({ value: v }))
          })) || []
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }, []);

  // Go to Shopify checkout
  const goToCheckout = useCallback(() => {
    if (cart?.webUrl) {
      window.location.href = cart.webUrl;
    }
  }, [cart]);

  // Calculate item count
  const itemCount = cart?.lineItems?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  // Calculate subtotal
  const subtotal = cart?.subtotalPrice?.amount || '0.00';
  
  // Flag to check if Shopify is enabled
  const isEnabled = !!client;

  return (
    <ShopifyContext.Provider value={{
      client,
      cart,
      cartOpen,
      setCartOpen,
      loading,
      error,
      isEnabled,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      resetCart,
      goToCheckout,
      fetchProduct,
      itemCount,
      subtotal,
    }}>
      {children}
    </ShopifyContext.Provider>
  );
};

export const useShopify = () => {
  const context = useContext(ShopifyContext);
  if (!context) {
    throw new Error('useShopify must be used within a ShopifyProvider');
  }
  return context;
};

export default ShopifyContext;
